import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDocs,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import DatePicker from 'react-multi-date-picker';
import './AddBookingDates.css';
import { Helmet } from 'react-helmet-async';

// CONSTANTES POUR LES FORMULAIRES
const CITIES = ['Marrakech', 'Rabat', 'Casablanca', 'Tanger', 'Fès', 'Agadir'];
const GENRES = ['Jazz', 'Blues', 'Rock', 'Pop', 'Classique', 'Variété', 'Oriental', 'Flamenco'];
const MUSICIAN_TYPES = ['Chanteur', 'Chanteuse', 'Guitariste', 'Pianiste', 'Batteur', 'Percussionniste', 'Violoniste', 'Oudiste', 'Bassiste', 'Groupe', 'DJ'];
const INSTRUMENTS = ['Piano', 'Guitare', 'Violon', 'Batterie', 'Saxophone', 'Flûte'];
const SPECIALTIES = ['Mariages', 'Événements corporatifs', 'Galas', 'Soirées privées', 'Mariages traditionnels', 'Henné', 'Événements culturels', 'Production technique', 'Concerts', 'Festivals', 'Conférences'];

export default function AddBookingDates() {
  // États existants pour les réservations...
  const [musicianId, setMusicianId] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [location, setLocation] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [estimatedRevenue, setEstimatedRevenue] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [status, setStatus] = useState('Réservé');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [messageType, setMessageType] = useState('success');

  // NOUVEAUX ÉTATS POUR LA SECTION PROFILS
  const [activeSection, setActiveSection] = useState('booking'); // 'booking' ou 'profile'
  const [profileType, setProfileType] = useState(''); // 'musician', 'studio', 'teacher'
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileStatusMessage, setProfileStatusMessage] = useState('');
  const [profileMessageType, setProfileMessageType] = useState('success');

  // États pour le formulaire musicien
  const [musicianForm, setMusicianForm] = useState({
    name: '',
    city: '',
    genres: [],
    type: '',
    bio: '',
    youtubeVideo: '',
    imageURL: '',
    performance: [],
    userId: '' // Optionnel
  });

  // États pour le formulaire studio
  const [studioForm, setStudioForm] = useState({
    name: '',
    city: '',
    description: '',
    pricePerHour: '',
    images: [''],
    equipment: []
  });

  // États pour le formulaire professeur
  const [teacherForm, setTeacherForm] = useState({
    name: '',
    city: '',
    instruments: [],
    experience: '',
    pricePerHour: '',
    image: '',
    bio: ''
  });

  // État pour le formulaire société d'événementiel
  const [eventCompanyForm, setEventCompanyForm] = useState({
    name: '',
    description: '',
    description_full: '',
    email: '',
    phone: '',
    website: '',
    location: '',
    address: '',
    logo: '',
    coverImage: '',
    rating: '',
    reviewCount: '',
    specialties: [],
    priceRange: '',
    minPrice: '',
    verified: false,
    active: true,
    featured: false,
    eventCount: '',
    yearsExperience: ''
  });

  // États existants pour les statistiques...
  const [stats, setStats] = useState({
    totalBookings: 0,
    monthlyRevenue: 0,
    confirmedBookings: 0,
    activeMusicians: 0
  });

  const [recentBookings, setRecentBookings] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const formatNameToId = (name) => {
    return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // Enlève les accents
      .replace(/[^a-zA-Z0-9]/g, '') // Enlève tout sauf lettres et chiffres
      .replace(/\s+/g, ''); // Enlève les espaces
  };

  // Fonctions existantes... (loadDashboardStats, handleSubmit, etc.)
  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setStatsLoading(true);
      const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
      const allBookings = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const totalBookings = allBookings.length;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const monthlyBookings = allBookings.filter(booking => {
        if (!booking.date) return false;
        const [day, month, year] = booking.date.split('/').map(Number);
        const bookingDate = new Date(year, month - 1, day);
        return bookingDate.getMonth() === currentMonth && 
               bookingDate.getFullYear() === currentYear;
      });

      const monthlyRevenue = monthlyBookings.reduce((sum, booking) => {
        return sum + (booking.estimatedRevenue || 0);
      }, 0);

      const confirmedBookings = allBookings.filter(booking => 
        booking.status === 'Confirmé' || booking.status === 'Réservé'
      ).length;

      const musiciansSnapshot = await getDocs(collection(db, 'Musiciens'));
      const activeMusicians = musiciansSnapshot.size;

      const sortedBookings = allBookings
        .filter(booking => booking.date)
        .sort((a, b) => {
          const [dayA, monthA, yearA] = a.date.split('/').map(Number);
          const [dayB, monthB, yearB] = b.date.split('/').map(Number);
          const dateA = new Date(yearA, monthA - 1, dayA);
          const dateB = new Date(yearB, monthB - 1, dayB);
          return dateB - dateA;
        })
        .slice(0, 5);

      const musiciansData = {};
      const musicianDocs = musiciansSnapshot.docs;
      musicianDocs.forEach(doc => {
        musiciansData[doc.id] = doc.data().name || doc.id;
      });

      const recentBookingsWithNames = sortedBookings.map(booking => ({
        id: booking.id,
        musician: musiciansData[booking.entityId] || booking.entityId,
        date: booking.date,
        status: booking.status || 'Non défini',
        revenue: booking.estimatedRevenue || 0
      }));

      setStats({
        totalBookings,
        monthlyRevenue,
        confirmedBookings,
        activeMusicians
      });

      setRecentBookings(recentBookingsWithNames);

    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchSuggestions(input) {
      if (!input || input.trim().length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const snapshot = await getDocs(collection(db, 'Musiciens'));
        const normalizedInput = input.toLowerCase();
        const results = snapshot.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || '',
          }))
          .filter(musician => musician.name.toLowerCase().includes(normalizedInput))
          .slice(0, 10);

        setSuggestions(results);
      } catch (error) {
        console.error('Erreur lors du chargement des musiciens :', error);
        setSuggestions([]);
      }
    }

    fetchSuggestions(musicianId);
  }, [musicianId]);

  const handleSuggestionClick = (musician) => {
    setMusicianId(musician.id);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    setMusicianId(e.target.value);
    setShowSuggestions(true);
  };

  // NOUVELLES FONCTIONS POUR LA GESTION DES PROFILS

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileStatusMessage('');

    try {
      let collectionName, formData, docId;

      switch (profileType) {
        case 'musician':
          collectionName = 'Musiciens';
          formData = { ...musicianForm };
          
          // Validation pour musicien
          if (!formData.name || !formData.city || formData.genres.length === 0) {
            setProfileStatusMessage('Veuillez remplir tous les champs obligatoires (nom, ville, genres).');
            setProfileMessageType('error');
            setProfileLoading(false);
            return;
          }

          // Traitement de l'image URL - ajout automatique du chemin
          if (formData.imageURL && !formData.imageURL.startsWith('http') && !formData.imageURL.startsWith('/')) {
            formData.imageURL = `/images/musiciens/${formData.imageURL}`;
          }
          
          // Utilise le nom formaté comme ID
          docId = formatNameToId(formData.name);
          if (!docId) {
            docId = `musician_${Date.now()}`; // Fallback si le nom est vide
          }
          break;

        case 'studio':
          collectionName = 'studios';
          formData = { ...studioForm, pricePerHour: parseFloat(studioForm.pricePerHour) || 0 };
          
          // Validation pour studio
          if (!formData.name || !formData.city) {
            setProfileStatusMessage('Veuillez remplir tous les champs obligatoires (nom, ville).');
            setProfileMessageType('error');
            setProfileLoading(false);
            return;
          }
          
          // Utilise le nom formaté comme ID
          docId = formatNameToId(formData.name);
          if (!docId) {
            docId = `studio_${Date.now()}`;
          }
          break;

        case 'teacher':
          collectionName = 'teachers';
          formData = { 
            ...teacherForm, 
            experience: parseInt(teacherForm.experience) || 0,
            pricePerHour: parseFloat(teacherForm.pricePerHour) || 0 
          };
          
          // Validation pour professeur
          if (!formData.name || !formData.city || formData.instruments.length === 0) {
            setProfileStatusMessage('Veuillez remplir tous les champs obligatoires (nom, ville, instruments).');
            setProfileMessageType('error');
            setProfileLoading(false);
            return;
          }
          
          // Utilise le nom formaté comme ID
          docId = formatNameToId(formData.name);
          if (!docId) {
            docId = `teacher_${Date.now()}`;
          }
          break;

        case 'eventCompany':
          collectionName = 'eventCompanies';
          formData = { 
            ...eventCompanyForm, 
            rating: parseFloat(eventCompanyForm.rating) || 0,
            reviewCount: parseInt(eventCompanyForm.reviewCount) || 0,
            minPrice: parseFloat(eventCompanyForm.minPrice) || 0,
            eventCount: parseInt(eventCompanyForm.eventCount) || 0,
            yearsExperience: parseInt(eventCompanyForm.yearsExperience) || 0
          };
          
          // Validation pour société d'événementiel
          if (!formData.name || !formData.location || formData.specialties.length === 0) {
            setProfileStatusMessage('Veuillez remplir tous les champs obligatoires (nom, localisation, spécialités).');
            setProfileMessageType('error');
            setProfileLoading(false);
            return;
          }
          
          // Utilise le nom formaté comme ID
          docId = formatNameToId(formData.name);
          if (!docId) {
            docId = `eventcompany_${Date.now()}`;
          }
          break;

        default:
          return;
      }

      // Ajouter timestamp
      formData.createdAt = new Date();

      const docRef = doc(db, collectionName, docId);
      await setDoc(docRef, formData);

      const typeLabels = {
        musician: 'Musicien',
        studio: 'Studio', 
        teacher: 'Professeur',
        eventCompany: 'Société d\'événementiel'
      };
      setProfileStatusMessage(`${typeLabels[profileType]} "${formData.name}" ajouté avec succès ! (ID: ${docId})`);
      setProfileMessageType('success');

      // Reset du formulaire
      if (profileType === 'musician') {
        setMusicianForm({
          name: '', city: '', genres: [], type: '', bio: '', youtubeVideo: '', imageURL: '', performance: [], userId: ''
        });
      } else if (profileType === 'studio') {
        setStudioForm({
          name: '', city: '', description: '', pricePerHour: '', images: [''], equipment: []
        });
      } else if (profileType === 'teacher') {
        setTeacherForm({
          name: '', city: '', instruments: [], experience: '', pricePerHour: '', image: '', bio: ''
        });
      } else if (profileType === 'eventCompany') {
        setEventCompanyForm({
          name: '', description: '', description_full: '', email: '', phone: '', website: '', 
          location: '', address: '', logo: '', coverImage: '', rating: '', reviewCount: '', 
          specialties: [], priceRange: '', minPrice: '', verified: false, active: true, 
          featured: false, eventCount: '', yearsExperience: ''
        });
      }

      // Recharger les statistiques
      loadDashboardStats();

    } catch (error) {
      console.error('Erreur lors de l\'ajout du profil:', error);
      setProfileStatusMessage('Erreur lors de l\'ajout du profil.');
      setProfileMessageType('error');
    } finally {
      setProfileLoading(false);
    }
  };

  const sendBookingNotification = async (musicianId, date, location) => {
    try {
      const musicianDoc = await getDoc(doc(db, 'Musiciens', musicianId));
      if (!musicianDoc.exists()) return;

      const musicianData = musicianDoc.data();

      // Sauvegarder la notification dans Firestore
      // La Cloud Function "sendPushOnNotification" enverra la push côté serveur
      const userId = musicianData.userId || null;
      const expoPushToken = musicianData.expoPushToken || null;
      const safeDate = date.replace(/\//g, '-');
      const notifId = `${musicianId}_${safeDate}_${Date.now()}`;
      await setDoc(doc(db, 'notifications', notifId), {
        musicianId,
        userId,
        expoPushToken,
        title: '🎵 Nouveau booking !',
        body: `Tu as une réservation le ${date}${location ? ` à ${location}` : ''}`,
        type: 'booking',
        date,
        location: location || '',
        read: false,
        createdAt: new Date(),
      });
    } catch (error) {
      console.error('Erreur envoi notification:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!musicianId || selectedDates.length === 0) {
      setStatusMessage('Veuillez remplir tous les champs obligatoires.');
      setMessageType('error');
      return;
    }

    const revenueAmount = estimatedRevenue ? parseFloat(estimatedRevenue) : 0;
    if (estimatedRevenue && (isNaN(revenueAmount) || revenueAmount < 0)) {
      setStatusMessage('❌ Le montant estimé doit être un nombre positif.');
      setMessageType('error');
      return;
    }

    const datesArray = selectedDates.map(d => d.format('DD/MM/YYYY'));
    setLoading(true);
    setStatusMessage('');

    try {
      const bookingsRef = collection(db, 'bookings');
      const alreadyReserved = [];

      for (const date of datesArray) {
        const q = query(bookingsRef, where('entityId', '==', musicianId), where('date', '==', date));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          alreadyReserved.push(date);
        }
      }

      const datesToAdd = datesArray.filter(date => !alreadyReserved.includes(date));

      for (const date of datesToAdd) {
        const safeDate = date.replace(/\//g, '-');
        const docId = `${musicianId}_${safeDate}`;
        const bookingDocRef = doc(db, 'bookings', docId);
        
        const bookingData = {
          entityId: musicianId,
          date,
          location,
          infoMessage,
          status,
          invoiced: false,
          invoiceId: null,
          estimatedRevenue: revenueAmount || 0,
          createdAt: new Date(),
        };

        await setDoc(bookingDocRef, bookingData);
      }

      for (const date of datesToAdd) {
        await sendBookingNotification(musicianId, date, location);
      }

      let message = '';
      let messageType = 'success';
      if (datesToAdd.length > 0) {
        message += `Ajouté ${datesToAdd.length} date(s) avec succès !`;
      }
      if (alreadyReserved.length > 0) {
        if (message) message += ' ';
        message += `Les dates suivantes étaient déjà réservées : ${alreadyReserved.join(', ')}`;
        if (datesToAdd.length === 0) {
          messageType = 'warning';
        } else {
          messageType = 'mixed';
        }
      }

      setStatusMessage(message);
      setMessageType(messageType);

      if (datesToAdd.length > 0) {
        setMusicianId('');
        setSelectedDates([]);
        setLocation('');
        setInfoMessage('');
        setEstimatedRevenue('');
        setSuggestions([]);
        setShowSuggestions(false);
        
        loadDashboardStats();
      }
    } catch (error) {
      console.error('Erreur ajout dates:', error);
      setStatusMessage('❌ Erreur lors de l\'ajout des dates.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // RENDER DES FORMULAIRES DE PROFILS

  const renderMusicianForm = () => (
    <form onSubmit={handleProfileSubmit} className="modern-form">
      {/* Nom */}
      <div className="form-group">
        <label className="form-label">Nom *</label>
        <input
          type="text"
          value={musicianForm.name}
          onChange={(e) => setMusicianForm({...musicianForm, name: e.target.value})}
          placeholder="Nom du musicien"
          required
          className="form-input"
        />
      </div>

      {/* Ville */}
      <div className="form-group">
        <label className="form-label">Ville *</label>
        <select
          value={musicianForm.city}
          onChange={(e) => setMusicianForm({...musicianForm, city: e.target.value})}
          required
          className="form-select"
        >
          <option value="">Choisir une ville</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      {/* Genres multiples */}
      <div className="form-group">
        <label className="form-label">Genres * (maintenir Ctrl pour sélection multiple)</label>
        <select
          multiple
          value={musicianForm.genres}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            setMusicianForm({...musicianForm, genres: selected});
          }}
          className="form-select"
          style={{ minHeight: '120px' }}
        >
          {GENRES.map(genre => (
            <option key={genre} value={genre}>{genre}</option>
          ))}
        </select>
        <small style={{ color: '#9ca3af', marginTop: '4px', display: 'block' }}>
          Sélectionnés: {musicianForm.genres.join(', ')}
        </small>
      </div>

      {/* Type */}
      <div className="form-group">
        <label className="form-label">Type</label>
        <select
          value={musicianForm.type}
          onChange={(e) => setMusicianForm({...musicianForm, type: e.target.value})}
          className="form-select"
        >
          <option value="">Choisir un type</option>
          {MUSICIAN_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Bio */}
      <div className="form-group">
        <label className="form-label">Biographie</label>
        <textarea
          value={musicianForm.bio}
          onChange={(e) => setMusicianForm({...musicianForm, bio: e.target.value})}
          placeholder="Description du musicien..."
          rows={4}
          className="form-textarea"
        />
      </div>

      {/* Image URL */}
      <div className="form-group">
        <label className="form-label">Image du musicien</label>
        <input
          type="text" 
          value={musicianForm.imageURL}
          onChange={(e) => setMusicianForm({...musicianForm, imageURL: e.target.value})}
          placeholder="image.jpg"
          className="form-input"
        />
        <small style={{ color: '#9ca3af', marginTop: '4px', display: 'block' }}>
          Entrez simplement le nom du fichier (ex: image.jpg) ou une URL complète
        </small>
      </div>

      {/* YouTube Video */}
      <div className="form-group">
        <label className="form-label">Vidéo YouTube</label>
        <input
          type="url"
          value={musicianForm.youtubeVideo}
          onChange={(e) => setMusicianForm({...musicianForm, youtubeVideo: e.target.value})}
          placeholder="https://www.youtube.com/watch?v=..."
          className="form-input"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={profileLoading}
        className="submit-button modern"
      >
        {profileLoading ? (
          <>
            <div className="loading-spinner"></div>
            Ajout en cours...
          </>
        ) : (
          'Ajouter le Musicien'
        )}
      </button>
    </form>
  );

  const renderStudioForm = () => (
    <form onSubmit={handleProfileSubmit} className="modern-form">
      <div className="form-group">
        <label className="form-label">Nom *</label>
        <input
          type="text"
          value={studioForm.name}
          onChange={(e) => setStudioForm({...studioForm, name: e.target.value})}
          placeholder="Nom du studio"
          required
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Ville *</label>
        <select
          value={studioForm.city}
          onChange={(e) => setStudioForm({...studioForm, city: e.target.value})}
          required
          className="form-select"
        >
          <option value="">Choisir une ville</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          value={studioForm.description}
          onChange={(e) => setStudioForm({...studioForm, description: e.target.value})}
          placeholder="Description du studio..."
          rows={4}
          className="form-textarea"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Prix par heure (DH)</label>
        <input
          type="number"
          value={studioForm.pricePerHour}
          onChange={(e) => setStudioForm({...studioForm, pricePerHour: e.target.value})}
          placeholder="500"
          min="0"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">URL de l'image principale</label>
        <input
          type="url"
          value={studioForm.images[0]}
          onChange={(e) => setStudioForm({...studioForm, images: [e.target.value]})}
          placeholder="https://exemple.com/studio.jpg"
          className="form-input"
        />
      </div>

      <button
        type="submit"
        disabled={profileLoading}
        className="submit-button modern"
      >
        {profileLoading ? (
          <>
            <div className="loading-spinner"></div>
            Ajout en cours...
          </>
        ) : (
          'Ajouter le Studio'
        )}
      </button>
    </form>
  );

  const renderTeacherForm = () => (
    <form onSubmit={handleProfileSubmit} className="modern-form">
      <div className="form-group">
        <label className="form-label">Nom *</label>
        <input
          type="text"
          value={teacherForm.name}
          onChange={(e) => setTeacherForm({...teacherForm, name: e.target.value})}
          placeholder="Nom du professeur"
          required
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Ville *</label>
        <select
          value={teacherForm.city}
          onChange={(e) => setTeacherForm({...teacherForm, city: e.target.value})}
          required
          className="form-select"
        >
          <option value="">Choisir une ville</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Instruments * (maintenir Ctrl pour sélection multiple)</label>
        <select
          multiple
          value={teacherForm.instruments}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            setTeacherForm({...teacherForm, instruments: selected});
          }}
          className="form-select"
          style={{ minHeight: '120px' }}
        >
          {INSTRUMENTS.map(instrument => (
            <option key={instrument} value={instrument}>{instrument}</option>
          ))}
        </select>
        <small style={{ color: '#9ca3af', marginTop: '4px', display: 'block' }}>
          Sélectionnés: {teacherForm.instruments.join(', ')}
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Expérience (années)</label>
        <input
          type="number"
          value={teacherForm.experience}
          onChange={(e) => setTeacherForm({...teacherForm, experience: e.target.value})}
          placeholder="5"
          min="0"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Prix par heure (DH)</label>
        <input
          type="number"
          value={teacherForm.pricePerHour}
          onChange={(e) => setTeacherForm({...teacherForm, pricePerHour: e.target.value})}
          placeholder="200"
          min="0"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">URL de l'image</label>
        <input
          type="url"
          value={teacherForm.image}
          onChange={(e) => setTeacherForm({...teacherForm, image: e.target.value})}
          placeholder="https://exemple.com/professeur.jpg"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Biographie</label>
        <textarea
          value={teacherForm.bio}
          onChange={(e) => setTeacherForm({...teacherForm, bio: e.target.value})}
          placeholder="Parcours et méthodes d'enseignement..."
          rows={4}
          className="form-textarea"
        />
      </div>

      <button
        type="submit"
        disabled={profileLoading}
        className="submit-button modern"
      >
        {profileLoading ? (
          <>
            <div className="loading-spinner"></div>
            Ajout en cours...
          </>
        ) : (
          'Ajouter le Professeur'
        )}
      </button>
    </form>
  );

  const renderEventCompanyForm = () => (
    <form onSubmit={handleProfileSubmit} className="modern-form">
      {/* Informations de base */}
      <div className="form-group">
        <label className="form-label">Nom de la société *</label>
        <input
          type="text"
          value={eventCompanyForm.name}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, name: e.target.value})}
          placeholder="Elite Events Maroc"
          required
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Localisation *</label>
        <select
          value={eventCompanyForm.location}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, location: e.target.value})}
          required
          className="form-select"
        >
          <option value="">Choisir une ville</option>
          {CITIES.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Description courte</label>
        <textarea
          value={eventCompanyForm.description}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, description: e.target.value})}
          placeholder="Description courte pour les cartes..."
          rows={3}
          className="form-textarea"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Description complète</label>
        <textarea
          value={eventCompanyForm.description_full}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, description_full: e.target.value})}
          placeholder="Description détaillée pour la page de profil..."
          rows={5}
          className="form-textarea"
        />
      </div>

      {/* Contact */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            value={eventCompanyForm.email}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, email: e.target.value})}
            placeholder="contact@société.ma"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Téléphone</label>
          <input
            type="tel"
            value={eventCompanyForm.phone}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, phone: e.target.value})}
            placeholder="+212 522 123 456"
            className="form-input"
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Site web</label>
        <input
          type="url"
          value={eventCompanyForm.website}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, website: e.target.value})}
          placeholder="https://société.ma"
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Adresse complète</label>
        <input
          type="text"
          value={eventCompanyForm.address}
          onChange={(e) => setEventCompanyForm({...eventCompanyForm, address: e.target.value})}
          placeholder="123 Rue Mohammed V, Casablanca"
          className="form-input"
        />
      </div>

      {/* Images */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">URL du logo</label>
          <input
            type="url"
            value={eventCompanyForm.logo}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, logo: e.target.value})}
            placeholder="https://exemple.com/logo.jpg"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">URL image de couverture</label>
          <input
            type="url"
            value={eventCompanyForm.coverImage}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, coverImage: e.target.value})}
            placeholder="https://exemple.com/cover.jpg"
            className="form-input"
          />
        </div>
      </div>

      {/* Spécialités */}
      <div className="form-group">
        <label className="form-label">Spécialités * (maintenir Ctrl pour sélection multiple)</label>
        <select
          multiple
          value={eventCompanyForm.specialties}
          onChange={(e) => {
            const selected = Array.from(e.target.selectedOptions, option => option.value);
            setEventCompanyForm({...eventCompanyForm, specialties: selected});
          }}
          className="form-select"
          style={{ minHeight: '120px' }}
        >
          {SPECIALTIES.map(specialty => (
            <option key={specialty} value={specialty}>{specialty}</option>
          ))}
        </select>
        <small style={{ color: '#9ca3af', marginTop: '4px', display: 'block' }}>
          Sélectionnées: {eventCompanyForm.specialties.join(', ')}
        </small>
      </div>

      {/* Évaluations et tarifs */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Note (0-5)</label>
          <input
            type="number"
            value={eventCompanyForm.rating}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, rating: e.target.value})}
            placeholder="4.8"
            min="0"
            max="5"
            step="0.1"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Nombre d'avis</label>
          <input
            type="number"
            value={eventCompanyForm.reviewCount}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, reviewCount: e.target.value})}
            placeholder="127"
            min="0"
            className="form-input"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Prix à partir de (MAD)</label>
          <input
            type="text"
            value={eventCompanyForm.priceRange}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, priceRange: e.target.value})}
            placeholder="15 000 MAD"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Prix minimum (MAD)</label>
          <input
            type="number"
            value={eventCompanyForm.minPrice}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, minPrice: e.target.value})}
            placeholder="15000"
            min="0"
            className="form-input"
          />
        </div>
      </div>

      {/* Statistiques */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Nombre d'événements réalisés</label>
          <input
            type="number"
            value={eventCompanyForm.eventCount}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, eventCount: e.target.value})}
            placeholder="250"
            min="0"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Années d'expérience</label>
          <input
            type="number"
            value={eventCompanyForm.yearsExperience}
            onChange={(e) => setEventCompanyForm({...eventCompanyForm, yearsExperience: e.target.value})}
            placeholder="10"
            min="0"
            className="form-input"
          />
        </div>
      </div>

      {/* Options */}
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              checked={eventCompanyForm.verified}
              onChange={(e) => setEventCompanyForm({...eventCompanyForm, verified: e.target.checked})}
              style={{ marginRight: '8px' }}
            />
            Société vérifiée
          </label>
        </div>

        <div className="form-group">
          <label className="form-label">
            <input
              type="checkbox"
              checked={eventCompanyForm.featured}
              onChange={(e) => setEventCompanyForm({...eventCompanyForm, featured: e.target.checked})}
              style={{ marginRight: '8px' }}
            />
            Société mise en avant
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={profileLoading}
        className="submit-button modern"
      >
        {profileLoading ? (
          <>
            <div className="loading-spinner"></div>
            Ajout en cours...
          </>
        ) : (
          'Ajouter la Société d\'Événementiel'
        )}
      </button>
    </form>
  );

  return (
    <div className="dashboard-container">
      <Helmet>
        <title>Dashboard Admin | Réservations & Profils</title>
        <meta name="description" content="Tableau de bord pour gérer les réservations et les profils" />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <div className="dashboard-header">
        <h1>Dashboard Administrateur</h1>
        <p>Gérez vos réservations, profils et suivez vos performances</p>
      </div>

      {/* Navigation entre sections */}
      <div className="section-navigation" style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px', 
        maxWidth: '1400px', 
        margin: '0 auto 32px' 
      }}>
        <button
          className={`tab ${activeSection === 'booking' ? 'active' : ''}`}
          onClick={() => setActiveSection('booking')}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: activeSection === 'booking' 
              ? 'linear-gradient(135deg, #ff7e03, #ffa726)' 
              : 'rgba(255, 255, 255, 0.05)',
            color: '#ffffff',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.3s ease'
          }}
        >
          📅 Réservations
        </button>
        <button
          className={`tab ${activeSection === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveSection('profile')}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: activeSection === 'profile' 
              ? 'linear-gradient(135deg, #ff7e03, #ffa726)' 
              : 'rgba(255, 255, 255, 0.05)',
            color: '#ffffff',
            cursor: 'pointer',
            fontWeight: '600',
            transition: 'all 0.3s ease'
          }}
        >
          👤 Ajouter un Profil
        </button>
      </div>

      {/* Stats Cards - toujours affichées */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-info">
              <p className="stat-title">Total Réservations</p>
              <p className="stat-value">
                {statsLoading ? '...' : stats.totalBookings}
              </p>
            </div>
            <div className="stat-icon calendar-icon">📅</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-info">
              <p className="stat-title">Revenus ce Mois</p>
              <p className="stat-value">
                {statsLoading ? '...' : `${stats.monthlyRevenue.toLocaleString()} DH`}
              </p>
            </div>
            <div className="stat-icon revenue-icon">💰</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-info">
              <p className="stat-title">Réservations Confirmées</p>
              <p className="stat-value">
                {statsLoading ? '...' : stats.confirmedBookings}
              </p>
            </div>
            <div className="stat-icon pending-icon">✅</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-info">
              <p className="stat-title">Musiciens Actifs</p>
              <p className="stat-value">
                {statsLoading ? '...' : stats.activeMusicians}
              </p>
            </div>
            <div className="stat-icon musicians-icon">🎵</div>
          </div>
        </div>
      </div>

      {/* Section Réservations */}
      {activeSection === 'booking' && (
        <div className="dashboard-content">
          {/* Formulaire Principal de Réservation */}
          <div className="main-content">
            <div className="booking-form-card">
              <div className="form-header">
                <h2>➕ Nouvelle Réservation</h2>
              </div>

              <form onSubmit={handleSubmit} className="modern-form">
                {/* Tous vos champs existants pour les réservations... */}
                <div className="form-group">
                  <label className="form-label">Musicien *</label>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      value={musicianId}
                      onChange={handleInputChange}
                      placeholder="Rechercher un musicien..."
                      required
                      className="form-input"
                      autoComplete="off"
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    />

                    {showSuggestions && suggestions.length > 0 && !isCalendarOpen && (
                      <div className="suggestions-dropdown modern">
                        {suggestions.map((musician, idx) => (
                          <div
                            key={idx}
                            onClick={() => handleSuggestionClick(musician)}
                            onMouseDown={(e) => e.preventDefault()}
                            className="suggestion-item"
                          >
                            <div className="musician-name">{musician.name}</div>
                            <div className="musician-id">{musician.id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* DatePicker */}
                <div className="form-group datepicker-wrapper">
                  <label className="form-label">Dates de réservation *</label>
                  
                  <DatePicker
                    multiple
                    value={selectedDates}
                    onChange={setSelectedDates}
                    format="DD/MM/YYYY"
                    placeholder="Choisir plusieurs dates"
                    className="modern-datepicker"
                    inputClass="form-input datepicker-input"
                    minDate={new Date()}
                    onOpen={() => setIsCalendarOpen(true)}
                    onClose={() => setIsCalendarOpen(false)}
                    renderButton={(direction, handleClick) => (
                      <button 
                        type="button"
                        onClick={handleClick}
                        className="custom-arrow-button"
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          transition: 'all 0.3s ease',
                          backdropFilter: 'blur(10px)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'rgba(255, 126, 3, 0.2)';
                          e.target.style.borderColor = '#ff7e03';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                          e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        {direction === "right" ? "❯" : "❮"}
                      </button>
                    )}
                  />

                  {selectedDates.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '0.875rem', color: '#10b981' }}>
                      Dates sélectionnées: {selectedDates.map(d => d.format('DD/MM/YYYY')).join(', ')}
                    </div>
                  )}
                </div>

                {/* Location et Status */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Localisation</label>
                    <input
                      type="text"
                      value={location}
                      onChange={e => setLocation(e.target.value)}
                      placeholder="Salle, Restaurant, Studio..."
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Statut</label>
                    <select
                      value={status}
                      onChange={e => setStatus(e.target.value)}
                      className="form-select"
                    >
                      <option value="Réservé">Réservé</option>
                      <option value="Option">Option</option>
                      <option value="Confirmé">Confirmé</option>
                      <option value="Annulé">Annulé</option>
                    </select>
                  </div>
                </div>

                {/* Revenue */}
                <div className="form-group">
                  <label className="form-label">Revenus estimés (DH)</label>
                  <input
                    type="number"
                    value={estimatedRevenue}
                    onChange={e => setEstimatedRevenue(e.target.value)}
                    placeholder="500"
                    min="0"
                    step="0.01"
                    className="form-input revenue-input"
                  />
                </div>

                {/* Message */}
                <div className="form-group">
                  <label className="form-label">Notes additionnelles</label>
                  <textarea
                    value={infoMessage}
                    onChange={e => setInfoMessage(e.target.value)}
                    placeholder="Détails ou informations supplémentaires..."
                    rows={3}
                    className="form-textarea"
                  />
                </div>

                {/* Preview des revenus */}
                {selectedDates.length > 0 && estimatedRevenue && (
                  <div className="revenue-preview modern">
                    <div className="preview-header">
                      <span className="preview-icon">📊</span>
                      <span className="preview-title">Aperçu financier</span>
                    </div>
                    <p className="preview-calculation">
                      <span className="dates-count">{selectedDates.length} date(s)</span> × 
                      <span className="rate"> {estimatedRevenue} DH</span> = 
                      <span className="total-amount">
                        {(selectedDates.length * parseFloat(estimatedRevenue || 0)).toFixed(2)} DH
                      </span>
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="submit-button modern"
                >
                  {loading ? (
                    <>
                      <div className="loading-spinner"></div>
                      Ajout en cours...
                    </>
                  ) : (
                    'Ajouter la réservation'
                  )}
                </button>
              </form>

              {/* Status Message */}
              {statusMessage && (
                <div className={`status-message modern ${
                  messageType === 'error' ? 'error' : 
                  messageType === 'warning' ? 'warning' : 
                  messageType === 'mixed' ? 'mixed' : 'success'
                }`}>
                  <span className="status-icon">
                    {messageType === 'error' ? '❌' : 
                    messageType === 'warning' ? '⚠️' : 
                    messageType === 'mixed' ? '⚠️' : '✅'}
                  </span>
                  <span className="status-text">{statusMessage}</span>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="sidebar">
            {/* Réservations récentes */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <h3>Réservations Récentes</h3>
                <span className="header-icon">👁️</span>
              </div>
              <div className="recent-bookings">
                {statsLoading ? (
                  <div className="loading-placeholder">Chargement...</div>
                ) : recentBookings.length > 0 ? (
                  recentBookings.map((booking) => (
                    <div key={booking.id} className="booking-item">
                      <div className="booking-info">
                        <p className="booking-musician">{booking.musician}</p>
                        <p className="booking-date">{booking.date}</p>
                      </div>
                      <div className="booking-details">
                        <p className="booking-revenue">{booking.revenue} DH</p>
                        <span className={`booking-status ${
                          booking.status === 'Confirmé' || booking.status === 'Réservé' 
                            ? 'confirmed' 
                            : 'pending'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-bookings">Aucune réservation récente</div>
                )}
              </div>
            </div>

            {/* Actions rapides */}
            <div className="sidebar-card">
              <h3 className="sidebar-title">Actions Rapides</h3>
              <div className="quick-actions">
                <button className="action-button" onClick={loadDashboardStats}>
                  <span className="action-icon">🔄</span>
                  <span className="action-text">Actualiser les Stats</span>
                </button>
                <button className="action-button">
                  <span className="action-icon">📅</span>
                  <span className="action-text">Voir Planning</span>
                </button>
                <button className="action-button">
                  <span className="action-icon">📊</span>
                  <span className="action-text">Générer Rapport</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Ajouter un Profil */}
      {activeSection === 'profile' && (
        <div className="dashboard-content" style={{ gridTemplateColumns: '1fr' }}>
          <div className="main-content">
            <div className="booking-form-card">
              <div className="form-header">
                <h2>👤 Ajouter un Profil</h2>
              </div>

              {/* Sélection du type de profil */}
              {!profileType && (
                <div className="profile-type-selection" style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '20px', 
                  marginBottom: '32px' 
                }}>
                  <button
                    onClick={() => setProfileType('musician')}
                    className="profile-type-button"
                    style={{
                      padding: '24px',
                      borderRadius: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#ff7e03';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎵</div>
                    <h3>Musicien</h3>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>
                      Ajouter un artiste ou groupe
                    </p>
                  </button>

                  <button
                    onClick={() => setProfileType('studio')}
                    className="profile-type-button"
                    style={{
                      padding: '24px',
                      borderRadius: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#ff7e03';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎙️</div>
                    <h3>Studio</h3>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>
                      Ajouter un studio d'enregistrement
                    </p>
                  </button>

                  <button
                    onClick={() => setProfileType('teacher')}
                    className="profile-type-button"
                    style={{
                      padding: '24px',
                      borderRadius: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#ff7e03';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎓</div>
                    <h3>Professeur</h3>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>
                      Ajouter un professeur de musique
                    </p>
                  </button>

                  <button
                    onClick={() => setProfileType('eventCompany')}
                    className="profile-type-button"
                    style={{
                      padding: '24px',
                      borderRadius: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#ff7e03';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎉</div>
                    <h3>Société d'Événementiel</h3>
                    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.875rem' }}>
                      Ajouter une société d'événementiel
                    </p>
                  </button>
                </div>
              )}

              {/* Bouton retour si un type est sélectionné */}
              {profileType && (
                <button
                  onClick={() => {
                    setProfileType('');
                    setProfileStatusMessage('');
                  }}
                  className="back-button"
                  style={{ position: 'relative' }}
                >
                  <span className="button-content">
                    Retour à la sélection
                  </span>
                </button>
              )}

              {/* Formulaires selon le type sélectionné */}
              {profileType === 'musician' && renderMusicianForm()}
              {profileType === 'studio' && renderStudioForm()}
              {profileType === 'teacher' && renderTeacherForm()}
              {profileType === 'eventCompany' && renderEventCompanyForm()}

              {/* Status Message pour profils */}
              {profileStatusMessage && (
                <div className={`status-message modern ${
                  profileMessageType === 'error' ? 'error' : 'success'
                }`}>
                  <span className="status-icon">
                    {profileMessageType === 'error' ? '❌' : '✅'}
                  </span>
                  <span className="status-text">{profileStatusMessage}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}